import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { poseTermCard, segmentPoseTerms } from './poseTerms';

const POPUP_WIDTH = 340;
const POPUP_MARGIN = 12;
const POPUP_GAP = 8;

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

function placePopup(anchorEl, panelEl) {
  if (!anchorEl) return null;
  const r = anchorEl.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.min(POPUP_WIDTH, vw - POPUP_MARGIN * 2);
  let left = clamp(r.left, POPUP_MARGIN, vw - width - POPUP_MARGIN);

  const spaceBelow = vh - r.bottom - POPUP_MARGIN;
  const spaceAbove = r.top - POPUP_MARGIN;
  const preferBelow = spaceBelow >= 220 || spaceBelow >= spaceAbove;
  const avail = preferBelow ? spaceBelow : spaceAbove;
  const maxHeight = Math.max(160, Math.min(avail - POPUP_GAP, vh - POPUP_MARGIN * 2));

  let top;
  if (preferBelow) {
    top = r.bottom + POPUP_GAP;
  } else {
    const measured = panelEl?.offsetHeight || maxHeight;
    top = Math.max(POPUP_MARGIN, r.top - POPUP_GAP - Math.min(measured, maxHeight));
  }

  // Keep the panel fully on-screen vertically.
  if (top + maxHeight > vh - POPUP_MARGIN) {
    top = Math.max(POPUP_MARGIN, vh - POPUP_MARGIN - maxHeight);
  }

  return {
    top,
    left,
    width,
    maxHeight,
  };
}

function PoseTermPopup({ card, onClose, anchorRef, id }) {
  const panelRef = useRef(null);
  const [coords, setCoords] = useState(null);

  useLayoutEffect(() => {
    const dialog = panelRef.current;
    if (!dialog) return undefined;

    // Top layer beats every page stacking context (lesson cards, nav, docks).
    // Bare <dialog> is display:none until opened — always force visible.
    try {
      if (typeof dialog.showModal === 'function') {
        if (!dialog.open) dialog.showModal();
      } else if (!dialog.open) {
        dialog.setAttribute('open', '');
      }
    } catch {
      dialog.setAttribute('open', '');
    }

    const update = () => {
      setCoords(placePopup(anchorRef?.current, panelRef.current));
    };
    update();
    // Second pass after content paints so flip-above uses real height.
    const raf = requestAnimationFrame(update);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      if (dialog.open) {
        try {
          dialog.close();
        } catch {
          /* ignore */
        }
      }
    };
  }, [anchorRef, card]);

  useEffect(() => {
    const dialog = panelRef.current;
    if (!dialog) return undefined;

    const onCancel = (e) => {
      e.preventDefault();
      onClose();
    };
    // Modal dialog makes the page inert — outside clicks hit the dialog/
    // backdrop, not window listeners. Treat a direct dialog hit as dismiss.
    const onDialogClick = (e) => {
      if (e.target === dialog) onClose();
    };
    const onPointer = (e) => {
      const anchor = anchorRef?.current;
      if (dialog.contains(e.target)) return;
      if (anchor?.contains(e.target)) return;
      onClose();
    };

    dialog.addEventListener('cancel', onCancel);
    dialog.addEventListener('click', onDialogClick);
    // Fallback when showModal is unavailable (non-modal fixed panel).
    window.addEventListener('pointerdown', onPointer);
    return () => {
      dialog.removeEventListener('cancel', onCancel);
      dialog.removeEventListener('click', onDialogClick);
      window.removeEventListener('pointerdown', onPointer);
    };
  }, [onClose, anchorRef]);

  if (!card || typeof document === 'undefined') return null;

  return createPortal(
    <dialog
      id={id}
      ref={panelRef}
      className="yoga-pose-term-popup m-fade-in"
      aria-label={`${card.displayName} pose description`}
      style={coords || { visibility: 'hidden' }}
    >
      <div className="yoga-pose-term-popup__head">
        <div style={{ minWidth: 0 }}>
          <div className="yoga-pose-term-popup__title">{card.displayName}</div>
          {card.sanskrit ? (
            <div className="yoga-pose-term-popup__sanskrit">{card.sanskrit}</div>
          ) : null}
        </div>
        <button
          type="button"
          className="cs-btn cs-btn-ghost cs-btn-sm"
          onClick={onClose}
          aria-label="Close pose description"
        >
          Close
        </button>
      </div>

      {card.thumb?.url ? (
        <img
          className="yoga-pose-term-popup__img"
          src={card.thumb.url}
          alt={card.thumb.alt || card.displayName}
          loading="lazy"
        />
      ) : (
        <div className="yoga-pose-term-popup__img yoga-pose-term-popup__img--empty" aria-hidden />
      )}

      <div className="u-cluster" style={{ '--gap': 'var(--sp-1)', marginTop: 'var(--sp-2)' }}>
        {card.difficulty ? <span className="cs-badge">{card.difficulty}</span> : null}
        {card.category ? (
          <span className="cs-badge">{String(card.category).replace(/_/g, ' ')}</span>
        ) : null}
      </div>

      {card.description ? (
        <p className="yoga-pose-term-popup__desc">{card.description}</p>
      ) : (
        <p className="yoga-pose-term-popup__desc u-muted">
          No written description in the dictionary yet — use the still as the shape reference.
        </p>
      )}

      <p className="u-muted u-small" style={{ margin: 'var(--sp-2) 0 0' }}>
        Lesson cue: <code>**Pose:** {card.cueKey}</code>
      </p>
    </dialog>,
    document.body,
  );
}

function PoseTermLink({ value, pose }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const popupId = useId();
  const card = poseTermCard(pose);

  if (!card) return value;

  const toggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen((v) => !v);
  };

  return (
    <span className="yoga-pose-term-wrap">
      <a
        ref={btnRef}
        href={`#pose-${card.pose.name}`}
        className="yoga-pose-term"
        aria-expanded={open}
        aria-controls={open ? popupId : undefined}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') toggle(e);
        }}
      >
        {value}
      </a>
      {open ? (
        <PoseTermPopup
          id={popupId}
          card={card}
          anchorRef={btnRef}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </span>
  );
}

/**
 * Render lesson / dictionary prose with Sanskrit (and other non-English pose
 * names) as hyperlinks that open a description + still pop-up.
 */
export default function PoseTermText({ text, as: As = 'span', className, style }) {
  const parts = segmentPoseTerms(text);
  return (
    <As className={className} style={style}>
      {parts.map((part, i) =>
        part.type === 'term' ? (
          <PoseTermLink key={`${part.value}-${i}`} value={part.value} pose={part.pose} />
        ) : (
          <React.Fragment key={i}>{part.value}</React.Fragment>
        ),
      )}
    </As>
  );
}

/** Standalone hyperlink for a known Sanskrit string (e.g. dictionary cards). */
export function PoseSanskritLink({ pose, children }) {
  const label = children || (pose?.sanskrit || []).join(' · ');
  if (!pose || !label) return null;
  return <PoseTermLink value={label} pose={pose} />;
}
